<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 700));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(135deg, #fdf6ec 0%, #f9e3d2 100%)",
      fontFamily:
        props.block.fontFamily ||
        "'Cormorant Garamond', 'Playfair Display', serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.6}px`,
    height: `${base * 2.6}px`,
    fontSize: `${base * 1.1}px`,
    radius: `${props.block.cardRadius ?? 999}px`,
  };
});
</script>

<template>
  <div class="cd-wedding" :style="containerStyle">
    <svg
      class="cd-wedding__floral cd-wedding__floral--l"
      viewBox="0 0 80 200"
      aria-hidden="true"
    >
      <path
        d="M40 5 C 60 40, 30 60, 50 90 S 30 140, 40 195"
        fill="none"
        stroke="#b08d57"
        stroke-width="0.8"
      />
      <circle
        cx="58"
        cy="40"
        r="6"
        fill="#fef3c7"
        stroke="#b08d57"
        stroke-width="0.6"
      />
      <circle
        cx="32"
        cy="80"
        r="5"
        fill="#fcd5ce"
        stroke="#b08d57"
        stroke-width="0.6"
      />
      <circle
        cx="48"
        cy="130"
        r="6"
        fill="#fef3c7"
        stroke="#b08d57"
        stroke-width="0.6"
      />
      <circle
        cx="34"
        cy="170"
        r="5"
        fill="#f1c0e8"
        stroke="#b08d57"
        stroke-width="0.6"
      />
    </svg>
    <svg
      class="cd-wedding__floral cd-wedding__floral--r"
      viewBox="0 0 80 200"
      aria-hidden="true"
    >
      <path
        d="M40 5 C 20 40, 50 60, 30 90 S 50 140, 40 195"
        fill="none"
        stroke="#b08d57"
        stroke-width="0.8"
      />
      <circle
        cx="22"
        cy="40"
        r="6"
        fill="#fef3c7"
        stroke="#b08d57"
        stroke-width="0.6"
      />
      <circle
        cx="48"
        cy="80"
        r="5"
        fill="#fcd5ce"
        stroke="#b08d57"
        stroke-width="0.6"
      />
      <circle
        cx="32"
        cy="130"
        r="6"
        fill="#fef3c7"
        stroke="#b08d57"
        stroke-width="0.6"
      />
      <circle
        cx="46"
        cy="170"
        r="5"
        fill="#f1c0e8"
        stroke="#b08d57"
        stroke-width="0.6"
      />
    </svg>

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-wedding__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 130}ms`,
      }"
    >
      <span
        class="cd-wedding__digit"
        :key="seg.value"
        :style="{
          fontSize: cardSize.fontSize,
          color: block.digitColor || '#704214',
        }"
      >
        {{ seg.value }}
      </span>
      <span class="cd-wedding__rule" aria-hidden="true" />
      <span
        class="cd-wedding__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || '#a07b3c',
        }"
      >
        {{ seg.label }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-wedding {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  overflow: hidden;
}
.cd-wedding__floral {
  position: absolute;
  height: 80%;
  top: 10%;
  width: auto;
  opacity: 0.65;
  pointer-events: none;
}
.cd-wedding__floral--l {
  left: 4%;
  transform: rotate(-4deg);
}
.cd-wedding__floral--r {
  right: 4%;
  transform: rotate(4deg) scaleX(-1);
}
.cd-wedding__cell {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(176, 141, 87, 0.45);
  box-shadow:
    0 12px 30px -12px rgba(176, 141, 87, 0.4),
    inset 0 0 0 6px rgba(255, 255, 255, 0.5);
  animation: cd-wed-rise 800ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
  transition: transform 0.4s ease;
}
.cd-wedding__cell:hover {
  transform: translateY(-3px);
}
@keyframes cd-wed-rise {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.cd-wedding__digit {
  font-weight: 500;
  font-style: italic;
  font-variant-numeric: tabular-nums;
  display: inline-block;
  animation: cd-wed-fade var(--cd-tick, 700ms) ease-out;
}
@keyframes cd-wed-fade {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.cd-wedding__rule {
  width: 36px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    #b08d57 30%,
    #b08d57 70%,
    transparent
  );
  margin: 4px 0 6px;
}
.cd-wedding__label {
  font-style: italic;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  font-weight: 500;
}
</style>
